/**
 * <s-sticky-atc> — add-to-cart bar that keeps a buy button on screen.
 *
 * Mobile (<768px): visible whenever the real add-to-cart button is off screen,
 * above or below, so shoppers can always buy without scrolling.
 * Desktop: the buy box is pinned, so the bar appears only after the real
 * button has scrolled above the viewport.
 *
 * It never owns cart state: its button clicks the real submit button inside
 * the product form, so the selected variant, quantity, loading state and
 * drawer opening all stay with <s-product-form>. Without JS the bar stays
 * hidden; its link falls back to jumping to the buy form.
 *
 * Required markup:
 *   data-target  — selector for the product form wrapper (stable across partial swaps).
 *   [data-sticky-atc-trigger] — link/button inside the bar.
 *   [data-sticky-atc-label]   — mirrors the real button's label (it includes the live price).
 */
class SStickyAtc extends HTMLElement {
  #ac;
  #frame = 0;
  #desktop = window.matchMedia('(min-width: 768px)');

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal, passive: true };
    window.addEventListener('scroll', this.#schedule, opts);
    window.addEventListener('resize', this.#schedule, opts);
    this.#desktop.addEventListener('change', this.#schedule, { signal: this.#ac.signal });
    this.querySelector('[data-sticky-atc-trigger]')?.addEventListener('click', this.#handleClick, { signal: this.#ac.signal });
    this.toggleAttribute('inert', true);
    this.#schedule();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#frame);
  }

  #form() {
    return document.querySelector(this.dataset.target ?? '#product-buy');
  }

  // Re-queried each time: the button lives inside a partial that is replaced on variant change.
  #realButton() {
    return this.#form()?.querySelector('[name="add"]') ?? null;
  }

  #schedule = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(this.#update);
  };

  #update = () => {
    const button = this.#realButton() ?? this.#form()?.querySelector('button');
    if (!button) return;
    const rect = button.getBoundingClientRect();
    const barHeight = this.offsetHeight;
    const inView = rect.bottom > 0 && rect.top < window.innerHeight - barHeight;
    const show = this.#desktop.matches ? rect.bottom < 0 : !inView;
    if (show === this.hasAttribute('data-visible')) return;
    this.toggleAttribute('data-visible', show);
    this.toggleAttribute('inert', !show);
    if (show) this.#syncLabel();
  };

  // Mirror only the live "Add to cart · price" text and the savings badge. The real button
  // also holds a struck-through compare price with a screen-reader label; copying its
  // flattened text would leak "Regular price" into the bar.
  #syncLabel() {
    const real = this.#realButton();
    const label = this.querySelector('[data-sticky-atc-label]');
    if (!real || !label) return;

    const copy = real.cloneNode(true);
    copy.querySelectorAll('s, .sr-only, [data-save-badge]').forEach((node) => node.remove());
    const text = copy.textContent.replace(/\s+/g, ' ').trim();
    if (text) label.textContent = text;

    const save = this.querySelector('[data-sticky-atc-save]');
    if (!save) return;
    const saveText = real.querySelector('[data-save-badge]')?.textContent.trim() ?? '';
    save.textContent = saveText;
    save.hidden = !saveText;
  }

  #handleClick = (event) => {
    const real = this.#realButton();
    if (!real) return; // Sold out or no form: let the link jump to the buy area.
    event.preventDefault();
    this.#syncLabel();
    real.click();
  };
}

if (!customElements.get('s-sticky-atc')) {
  customElements.define('s-sticky-atc', SStickyAtc);
}
